// utils/groupEvents.ts

import { GameLog } from '../../../Logs/LogsDisplay';

export interface TimelineEvent {
  type: 'KeyPressGroup' | 'StackTimeoutGroup' | 'TakeDownGroup' | 'KeyPressInterrupt';
  timestamp: number;
  eventData: any;
}

export interface TakeDownGroup {
  takeDownEvent: GameLog;
  relatedEvents: GameLog[];
}

// utils/groupEvents.ts

export interface CombinedEventGroup {
  keyPress: GameLog;
  damageEvents: GameLog[];
  incomingDamageEvents: GameLog[]; // Damage dealt BACK to the caster (e.g. Rammus W reflect, Unending Despair)
  casterStatEvents: GameLog[];
  targetStatEventsByChampion: { [championId: string]: GameLog[] };
  stackUpdates: GameLog[];
  casterCCEvents: GameLog[];
  targetCCEventsByChampion: { [championId: string]: GameLog[] };
  targetDebuffEventsByChampion: { [championId: string]: GameLog[] };
  cooldownReductionEvents: GameLog[];
  damageMultiplierEvents: GameLog[];
}

//Dont forget there is some prefiltering for each log per champion in the GameGrid

export function processLogsToTimelineEvents(logs: GameLog[]): TimelineEvent[] {
  const timelineEvents: TimelineEvent[] = [];
  let stackTimeoutBuffer: GameLog[] = [];

  // Group events by sequenceId to ensure events stay with their related KeyPress
  const eventsBySequenceId: Record<string, GameLog[]> = {};
  
  // First, group all events by sequenceId
  logs.forEach(log => {
    if (!log.sequenceId) return;
    
    if (!eventsBySequenceId[log.sequenceId]) {
      eventsBySequenceId[log.sequenceId] = [];
    }
    eventsBySequenceId[log.sequenceId].push(log);
  });
  
  // Now process each sequence group separately
  Object.keys(eventsBySequenceId).forEach(sequenceId => {
    const sequenceLogs = eventsBySequenceId[sequenceId].sort((a, b) => a.timestamp - b.timestamp);
    let localStackTimeoutBuffer: GameLog[] = [];

    const traverse = (events: GameLog[]) => {
      events.forEach((event) => {
        if (event.type === 'KeyPress') {
          // Before processing the KeyPress event, check if there are any buffered StackTimeouts
          if (localStackTimeoutBuffer.length > 0) {
            timelineEvents.push({
              type: 'StackTimeoutGroup',
              timestamp: localStackTimeoutBuffer[0].timestamp,
              eventData: localStackTimeoutBuffer,
            });
            localStackTimeoutBuffer = [];
          }

          const group: CombinedEventGroup = {
            keyPress: event,
            damageEvents: [],
            incomingDamageEvents: [],
            casterStatEvents: [],
            targetStatEventsByChampion: {},
            stackUpdates: [],
            casterCCEvents: [],
            targetCCEventsByChampion: {},
            targetDebuffEventsByChampion: {},
            cooldownReductionEvents: [],
            damageMultiplierEvents: []
          };

          if (event.childEvents && event.childEvents.length > 0) {
            processChildEvents(event.childEvents, group);
          }

          const timelineEvent: TimelineEvent = {
            type: 'KeyPressGroup',
            timestamp: event.timestamp,
            eventData: group,
          };

          timelineEvents.push(timelineEvent);
        } else if (event.type === 'KeyPressInterrupt') {
          // Before processing the KeyPressInterrupt event, check if there are any buffered StackTimeouts
          if (localStackTimeoutBuffer.length > 0) {
            timelineEvents.push({
              type: 'StackTimeoutGroup',
              timestamp: localStackTimeoutBuffer[0].timestamp,
              eventData: localStackTimeoutBuffer,
            });
            localStackTimeoutBuffer = [];
          }

          // KeyPressInterrupt is its own timeline event
          const timelineEvent: TimelineEvent = {
            type: 'KeyPressInterrupt',
            timestamp: event.timestamp,
            eventData: event,
          };

          timelineEvents.push(timelineEvent);
        } else if (event.type === 'StackTimeout') {
          // Collect StackTimeout events into buffer
          localStackTimeoutBuffer.push(event);
        }
        else if (event.type === 'TakeDown') {
          // Before processing the TakeDown event, check if there are any buffered StackTimeouts
          if (localStackTimeoutBuffer.length > 0) {
            timelineEvents.push({
              type: 'StackTimeoutGroup',
              timestamp: localStackTimeoutBuffer[0].timestamp,
              eventData: localStackTimeoutBuffer,
            });
            localStackTimeoutBuffer = [];
          }

          // Create a TakeDownGroup event
          const group: TakeDownGroup = {
            takeDownEvent: event,
            relatedEvents: [],
          };

          // Process child events if any
          if (event.childEvents && event.childEvents.length > 0) {
            processTakeDownChildEvents(event.childEvents, group);
          }

          const timelineEvent: TimelineEvent = {
            type: 'TakeDownGroup',
            timestamp: event.timestamp,
            eventData: group,
          };

          timelineEvents.push(timelineEvent);
        } else {
          // Before processing other events, check if there are any buffered StackTimeouts
          if (localStackTimeoutBuffer.length > 0) {
            timelineEvents.push({
              type: 'StackTimeoutGroup',
              timestamp: localStackTimeoutBuffer[0].timestamp,
              eventData: localStackTimeoutBuffer,
            });
            localStackTimeoutBuffer = [];
          }

          // Process child events if any
          if (event.childEvents && event.childEvents.length > 0) {
            traverse(event.childEvents);
          }
        }
      });
    };

    const processChildEvents = (events: GameLog[], group: CombinedEventGroup) => {
      const casterChampionId = group.keyPress.details.championId;

      events.forEach((event) => {
        // Only process child events with matching sequenceId
        if (event.sequenceId && event.sequenceId !== sequenceId) return;
        
        if (event.type === 'StackUpdate') {
          group.stackUpdates.push(event);
        } else if (event.type === 'DamageDealt' || event.type === 'DamageTaken') {
          // Separate reflected/reactive damage (e.g. Rammus W, Unending Despair) into its own category
          if (event.type === 'DamageTaken' && event.details.attackerId && event.details.attackerId !== casterChampionId) {
            // Damage dealt BACK to the caster by the target (reflected/reactive damage)
            group.incomingDamageEvents.push(event);
          } else {
            group.damageEvents.push(event);
          }
        } else if (event.type === "ShieldMitigation") {
          group.damageEvents.push(event);
        } else if (event.type === 'Healing' || event.type === 'StatChange') {
          const targetChampionId = event.details.targetId;
          const eventChampionId = event.details.championId;

          // Determine if the event affects the caster or a target based on ID comparison
          if (targetChampionId) {
            // Target is specified - use targetId to determine grouping
            if (targetChampionId === casterChampionId) {
              // Event affects the caster (self-buff or self-effect)
              group.casterStatEvents.push(event);
            } else {
              // Event affects a different champion (cross-champion effect like Jarvan Q reducing enemy armor)
              if (!group.targetStatEventsByChampion[targetChampionId]) {
                group.targetStatEventsByChampion[targetChampionId] = [];
              }
              group.targetStatEventsByChampion[targetChampionId].push(event);
            }
          } else {
            // No targetId specified - fall back to championId
            if (eventChampionId === casterChampionId) {
              // Event affects the caster
              group.casterStatEvents.push(event);
            } else if (eventChampionId) {
              // Event affects another champion (target event)
              if (!group.targetStatEventsByChampion[eventChampionId]) {
                group.targetStatEventsByChampion[eventChampionId] = [];
              }
              group.targetStatEventsByChampion[eventChampionId].push(event);
            }
          }
        } else if (event.type === 'CrowdControl') {
          const targetChampionId = event.details.targetChampionId;
          const eventChampionId = event.details.sourceChampionId;

          // Determine if the event affects the caster or a target
          if (targetChampionId) {
            // Target is specified
            if (targetChampionId === casterChampionId) {
              // Event affects the caster
              group.casterCCEvents.push(event);
            } else {
              // Event affects a target
              if (!group.targetCCEventsByChampion[targetChampionId]) {
                group.targetCCEventsByChampion[targetChampionId] = [];
              }
              group.targetCCEventsByChampion[targetChampionId].push(event);
            }
          } else {
            // No target specified
            if (eventChampionId === casterChampionId) {
              // Event affects the caster
              group.casterCCEvents.push(event);
            } else if (eventChampionId) {
              // Event affects another champion (target event)
              if (!group.targetCCEventsByChampion[eventChampionId]) {
                group.targetCCEventsByChampion[eventChampionId] = [];
              }
              group.targetCCEventsByChampion[eventChampionId].push(event);
            }
          }
        } else if (event.type === 'DebuffApplied') {
          const targetChampionId = event.details.targetId;

          // Debuffs are always target events (applied to someone)
          if (targetChampionId) {
            if (!group.targetDebuffEventsByChampion[targetChampionId]) {
              group.targetDebuffEventsByChampion[targetChampionId] = [];
            }
            group.targetDebuffEventsByChampion[targetChampionId].push(event);
          }
        } else if (event.type === 'StackTimeout') {
          // StackTimeout events outside of KeyPressGroups are buffered
          localStackTimeoutBuffer.push(event);
        } else if (event.type === 'CooldownReduction') {
          group.cooldownReductionEvents.push(event);
    
        } 
        
        else if (event.type === 'DamageMultiplier') {
            const source = event.details.source;
            const percent = event.details.damageIncreasePercent;
            const extraDamage = event.details.extraDamage || 0;
          
            // Check if we already have an event with same source & percentage
            const existing = group.damageMultiplierEvents.find(em => {
              return (
                em.details.source === source &&
                em.details.damageIncreasePercent === percent
              );
            });
          
            if (!existing) {
              // If not found, create new event with aggregation tracking
              const newEvent = {
                ...event,
                details: {
                  ...event.details,
                  totalExtraDamage: extraDamage,
                  instanceCount: 1,
                  instances: [{
                    extraDamage: extraDamage,
                    timestamp: event.timestamp,
                    id: event.id,
                    damageInstanceName: event.details.damageInstanceName
                  }]
                }
              };
              group.damageMultiplierEvents.push(newEvent);
            } else {
              // If found, aggregate the extra damage
              existing.details.totalExtraDamage = (existing.details.totalExtraDamage || 0) + extraDamage;
              existing.details.instanceCount = (existing.details.instanceCount || 1) + 1;
              existing.details.instances = existing.details.instances || [];
              existing.details.instances.push({
                extraDamage: extraDamage,
                timestamp: event.timestamp,
                id: event.id,
                damageInstanceName: event.details.damageInstanceName
              });
            }
          }
          else {
          group.damageEvents.push(event); // Other events can be added to damageEvents or handled separately
        }

        // Process child events
        if (event.childEvents && event.childEvents.length > 0) {
          processChildEvents(event.childEvents, group);
        }
      });
    };

    const processTakeDownChildEvents = (events: GameLog[], group: TakeDownGroup) => {
      events.forEach((event) => {
        // Only process child events with matching sequenceId
        if (event.sequenceId && event.sequenceId !== sequenceId) return;
        
        // Add all events to relatedEvents - let the event renderer handle the display
        group.relatedEvents.push(event);

        // Process child events recursively
        if (event.childEvents && event.childEvents.length > 0) {
          processTakeDownChildEvents(event.childEvents, group);
        }
      });
    };

    traverse(sequenceLogs);

    // After traversal, check if there are any remaining StackTimeouts
    if (localStackTimeoutBuffer.length > 0) {
      timelineEvents.push({
        type: 'StackTimeoutGroup',
        timestamp: localStackTimeoutBuffer[0].timestamp,
        eventData: localStackTimeoutBuffer,
      });
    }
  });

  // Sort timeline events by timestamp
  return timelineEvents.sort((a, b) => a.timestamp - b.timestamp);
}
